import { AlgorithmTraceCallback, Chromosome, GenerationProgressCallback } from '../types/algorithm';
import type { SflagaEnhancementOptions } from '../types';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import { resolveSflagaEnhancements } from '../utils/parameterUtils';
import {
  initializePopulation,
  tournamentSelection,
  singlePointCrossover,
  findBestChromosome,
} from './geneticAlgorithm';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';
import {
  incrementLocalSearchCalls,
  incrementMutationCalls,
} from '../utils/benchmarkInstrumentation';

/**
 * Proposed enhanced SFLA-GA hybrid.
 *
 * Relative to the standard SFLA-GA baseline, this variant adds stronger local
 * refinement, guided mutation, adaptive diversification, and conservative
 * elite preservation. These mechanisms are kept explicit so the benchmark can
 * distinguish the baseline hybrid from the proposed enhancement.
 */

const LOCAL_EVOLUTION_STEPS_PER_MEMEPLEX = 3;

function isSimilarEnough(
  chrom1: Chromosome,
  chrom2: Chromosome,
  threshold: number
): boolean {
  const maxMismatches = Math.floor(chrom1.x.length * (1 - threshold));
  let mismatches = 0;

  for (let i = 0; i < chrom1.x.length; i++) {
    if (chrom1.x[i] !== chrom2.x[i]) {
      mismatches++;
      if (mismatches > maxMismatches) {
        return false;
      }
    }
  }

  return true;
}

function isTooSimilarToElites(
  chrom: Chromosome,
  elites: Chromosome[],
  threshold: number = 0.9
): boolean {
  for (const elite of elites) {
    if (isSimilarEnough(chrom, elite, threshold)) {
      return true;
    }
  }
  return false;
}

function mutateChromosome(
  chromosome: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  rng: SeededRNG,
  guidedMutation: boolean,
  heavyMode: boolean = false,
  topCandidates: number = 5
): Chromosome {
  const mutated: Chromosome = {
    x: [...chromosome.x],
    fitness: 0,
    rwp: 0,
  };

  const mutationCount = Math.min(
    taskCount,
    rng.randomIntRange(1, heavyMode ? 3 : taskCount >= 500 ? 2 : 3)
  );
  const mutatedIndices = new Set<number>();
  const effectiveTopCandidates = heavyMode
    ? topCandidates
    : taskCount >= 1000
      ? 2
      : taskCount >= 250
        ? 3
        : topCandidates;

  for (let i = 0; i < mutationCount; i++) {
    incrementMutationCalls();
    let taskIdx: number;
    do {
      taskIdx = rng.randomInt(taskCount);
    } while (mutatedIndices.has(taskIdx));
    mutatedIndices.add(taskIdx);

    if (!guidedMutation) {
      mutated.x[taskIdx] = rng.randomInt(serviceCount);
      continue;
    }

    const candidateCount = Math.min(effectiveTopCandidates, serviceCount);
    const candidates: Array<{ service: number; fitness: number }> = [];
    const candidateIndices = new Set<number>();

    while (candidateIndices.size < candidateCount) {
      const serviceIdx = rng.randomInt(serviceCount);
      if (candidateIndices.has(serviceIdx)) {
        continue;
      }

      candidateIndices.add(serviceIdx);

      const testChromosome: Chromosome = {
        x: [...mutated.x],
        fitness: 0,
        rwp: 0,
      };
      testChromosome.x[taskIdx] = serviceIdx;
      calculateChromosomeFitness(testChromosome, matrix, taskCount, serviceCount);
      candidates.push({ service: serviceIdx, fitness: testChromosome.fitness });
    }

    candidates.sort((a, b) => b.fitness - a.fitness);
    mutated.x[taskIdx] =
      candidates.length > 0 ? candidates[0].service : rng.randomInt(serviceCount);
  }

  return mutated;
}

function createMemeplexes(
  population: Chromosome[],
  memeplexCount: number
): Chromosome[][] {
  const memeplexes: Chromosome[][] = Array(memeplexCount)
    .fill(null)
    .map(() => []);

  for (let i = 0; i < population.length; i++) {
    memeplexes[i % memeplexCount].push(population[i]);
  }

  return memeplexes;
}

function shuffleMemeplexes(
  memeplexes: Chromosome[][],
  population: Chromosome[]
): void {
  const memeplexCount = memeplexes.length;

  for (let i = 0; i < population.length; i++) {
    const memeplexIdx = i % memeplexCount;
    const frogIdx = Math.floor(i / memeplexCount);

    if (frogIdx < memeplexes[memeplexIdx].length) {
      population[i] = memeplexes[memeplexIdx][frogIdx];
    }
  }
}

function localSearchWorstFrog(
  worst: Chromosome,
  best: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  rng: SeededRNG
): Chromosome {
  const maxStep = Math.max(1, Math.min(5, Math.floor(taskCount / 10)));
  let bestResult: Chromosome | null = null;
  let bestFitness = worst.fitness;
  const attempts = LOCAL_EVOLUTION_STEPS_PER_MEMEPLEX;

  for (let attempt = 0; attempt < attempts; attempt++) {
    incrementLocalSearchCalls();
    const step =
      attempt === 0 ? rng.randomIntRange(1, maxStep) : Math.min(maxStep, 1 + attempt);

    const newFrog: Chromosome = {
      x: [...worst.x],
      fitness: 0,
      rwp: 0,
    };

    const copiedIndices = new Set<number>();
    for (let i = 0; i < step && copiedIndices.size < taskCount; i++) {
      let taskIdx: number;
      do {
        taskIdx = rng.randomInt(taskCount);
      } while (copiedIndices.has(taskIdx));

      copiedIndices.add(taskIdx);
      newFrog.x[taskIdx] = best.x[taskIdx];
    }

    calculateChromosomeFitness(newFrog, matrix, taskCount, serviceCount);
    if (newFrog.fitness > bestFitness) {
      bestFitness = newFrog.fitness;
      bestResult = newFrog;
    }
  }

  return bestResult || worst;
}

function selectParent(
  population: Chromosome[],
  selectionRate: number,
  rng: SeededRNG,
  tournamentSize: number
): Chromosome {
  if (rng.randomChance(selectionRate / 100)) {
    return population[tournamentSelection(population, rng, tournamentSize)];
  }

  return population[rng.randomInt(population.length)];
}

function evolveMemeplex(
  memeplex: Chromosome[],
  globalBest: Chromosome,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  heavyMode: boolean,
  enhancements: SflagaEnhancementOptions,
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number,
  generation: number,
  maxGenerations: number,
  rng: SeededRNG,
  memeplexIndex: number = 0,
  trace?: AlgorithmTraceCallback
): void {
  if (memeplex.length === 0) return;

  const memeplexSize = memeplex.length;

  memeplex.sort((a, b) => b.fitness - a.fitness);
  const initialMemeplexBest = memeplex[0];
  const memeplexWorst = memeplex[memeplexSize - 1];
  const eliteCount = enhancements.elitePreservation
    ? Math.max(1, Math.floor(memeplexSize * 0.02))
    : 0;
  const availableSlots = Math.max(0, memeplexSize - eliteCount);
  const crossoverCount =
    availableSlots > 0
      ? Math.min(
          Math.max(1, Math.floor(availableSlots / 2)),
          Math.max(1, Math.floor((availableSlots * crossoverRate) / 100))
        )
      : 0;
  const mutateCount =
    availableSlots > 0
      ? Math.min(
          Math.max(1, Math.floor((availableSlots * mutationRate) / 100)),
          heavyMode ? 3 : taskCount >= 1000 ? 1 : taskCount >= 250 ? 2 : 3
        )
      : 0;
  let localSearchChanged = false;

  if (enhancements.worstFrogLocalSearch && memeplexSize > 2) {
    let improved = localSearchWorstFrog(
      memeplexWorst,
      initialMemeplexBest,
      taskCount,
      serviceCount,
      matrix,
      rng
    );
    trace?.({
      generation: generation + 1,
      phase: 'local_search',
      action: 'try_memeplex_best',
      detail: 'Attempted enhanced local search on the worst frog using the memeplex best frog.',
      entityIndex: memeplexIndex,
      previousFitness: memeplexWorst.fitness,
      fitness: improved.fitness,
      accepted: improved.fitness > memeplexWorst.fitness,
    });

    if (improved.fitness > memeplexWorst.fitness) {
      const significantImprovement =
        memeplexWorst.fitness <= 0 ||
        improved.fitness / memeplexWorst.fitness > 1.05;

      if (!significantImprovement) {
        const improvedWithGlobal = localSearchWorstFrog(
          memeplexWorst,
          globalBest,
          taskCount,
          serviceCount,
          matrix,
          rng
        );
        trace?.({
          generation: generation + 1,
          phase: 'local_search',
          action: 'try_global_best_after_small_gain',
          detail: 'Retried local search with the global best because memeplex-best improvement was small.',
          entityIndex: memeplexIndex,
          previousFitness: memeplexWorst.fitness,
          fitness: improvedWithGlobal.fitness,
          accepted: improvedWithGlobal.fitness > improved.fitness,
        });
        if (improvedWithGlobal.fitness > improved.fitness) {
          improved = improvedWithGlobal;
        }
      }

      memeplex[memeplexSize - 1] = improved;
      localSearchChanged = true;
    } else {
      improved = localSearchWorstFrog(
        memeplexWorst,
        globalBest,
        taskCount,
        serviceCount,
        matrix,
        rng
      );
      trace?.({
        generation: generation + 1,
        phase: 'local_search',
        action: 'try_global_best',
        detail: 'Attempted enhanced local search on the worst frog using the global best frog.',
        entityIndex: memeplexIndex,
        previousFitness: memeplexWorst.fitness,
        fitness: improved.fitness,
        accepted: improved.fitness > memeplexWorst.fitness,
      });

      if (improved.fitness > memeplexWorst.fitness) {
        memeplex[memeplexSize - 1] = improved;
        localSearchChanged = true;
      } else {
        const hybridFrog: Chromosome = {
          x: [...memeplexWorst.x],
          fitness: 0,
          rwp: 0,
        };

        const replaceCount = Math.max(1, Math.floor(taskCount / 2));
        const replacedIndices = new Set<number>();
        for (let j = 0; j < replaceCount; j++) {
          let taskIdx: number;
          do {
            taskIdx = rng.randomInt(taskCount);
          } while (replacedIndices.has(taskIdx));

          replacedIndices.add(taskIdx);
          hybridFrog.x[taskIdx] = rng.randomInt(serviceCount);
        }

        calculateChromosomeFitness(hybridFrog, matrix, taskCount, serviceCount);
        if (hybridFrog.fitness > memeplexWorst.fitness) {
          memeplex[memeplexSize - 1] = hybridFrog;
          localSearchChanged = true;
          trace?.({
            generation: generation + 1,
            phase: 'replacement',
            action: 'hybrid_random_replacement',
            detail: 'Replaced the worst frog with a partially randomized hybrid frog.',
            entityIndex: memeplexIndex,
            previousFitness: memeplexWorst.fitness,
            fitness: hybridFrog.fitness,
            accepted: true,
            solution: hybridFrog.x,
          });
        }
      }
    }
  }

  if (localSearchChanged) {
    memeplex.sort((a, b) => b.fitness - a.fitness);
  }

  const memeplexBest = memeplex[0];
  const tournamentSize = memeplexSize >= 4 ? 4 : 3;
  const elites = memeplex.slice(0, eliteCount);
  const children: Chromosome[] = [];
  const globalBestUsageRate =
    0.5 + generation / Math.max(1, maxGenerations) * 0.2;

  for (let i = 0; i < crossoverCount; i++) {
    let parent1: Chromosome;
    let parent2: Chromosome;

    if (rng.randomChance(globalBestUsageRate)) {
      parent1 = globalBest;
      parent2 = selectParent(memeplex, selectionRate, rng, tournamentSize);
    } else if (rng.randomChance(0.3)) {
      parent1 = memeplexBest;
      parent2 = selectParent(memeplex, selectionRate, rng, tournamentSize);
    } else {
      parent1 = selectParent(memeplex, selectionRate, rng, tournamentSize);
      parent2 = selectParent(memeplex, selectionRate, rng, tournamentSize);
    }
    trace?.({
      generation: generation + 1,
      phase: 'selection',
      action: 'select_guided_parents',
      detail: 'Selected guided SFLAGA parents using global best, memeplex best, or tournament policy.',
      entityIndex: memeplexIndex,
      metadata: {
        parent1Fitness: parent1.fitness,
        parent2Fitness: parent2.fitness,
        globalBestUsageRate,
      },
    });

    let [child1, child2] = singlePointCrossover(parent1, parent2, taskCount, rng);
    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);
    trace?.({
      generation: generation + 1,
      phase: 'crossover',
      action: 'single_point_crossover',
      detail: 'Created and evaluated two guided SFLAGA crossover children.',
      entityIndex: memeplexIndex,
      fitness: Math.max(child1.fitness, child2.fitness),
      metadata: { child1Fitness: child1.fitness, child2Fitness: child2.fitness },
    });

    if (
      enhancements.similarityAwareDiversification &&
      elites.length > 0 &&
      isTooSimilarToElites(child1, elites)
    ) {
      const mutated = mutateChromosome(
        child1,
        taskCount,
        serviceCount,
        matrix,
        rng,
        enhancements.guidedMutation,
        heavyMode
      );
      calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);
      if (mutated.fitness >= child1.fitness) {
        child1 = mutated;
        trace?.({
          generation: generation + 1,
          phase: 'diversification',
          action: 'diversify_similar_child',
          detail: 'Diversified child 1 because it was too similar to elites.',
          entityIndex: memeplexIndex,
          fitness: child1.fitness,
          accepted: true,
        });
      }
    }

    if (
      enhancements.similarityAwareDiversification &&
      elites.length > 0 &&
      isTooSimilarToElites(child2, elites)
    ) {
      const mutated = mutateChromosome(
        child2,
        taskCount,
        serviceCount,
        matrix,
        rng,
        enhancements.guidedMutation,
        heavyMode
      );
      calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);
      if (mutated.fitness >= child2.fitness) {
        child2 = mutated;
        trace?.({
          generation: generation + 1,
          phase: 'diversification',
          action: 'diversify_similar_child',
          detail: 'Diversified child 2 because it was too similar to elites.',
          entityIndex: memeplexIndex,
          fitness: child2.fitness,
          accepted: true,
        });
      }
    }

    children.push(child1, child2);
  }

  children.sort((a, b) => b.fitness - a.fitness);

  let childIdx = 0;
  let memeplexIdx = memeplexSize - 1;
  let crossoverChanged = false;
  while (childIdx < children.length && memeplexIdx >= eliteCount) {
    if (children[childIdx].fitness > memeplex[memeplexIdx].fitness) {
      trace?.({
        generation: generation + 1,
        phase: 'replacement',
        action: 'replace_memeplex_member',
        detail: 'Replaced a non-elite memeplex frog with a fitter crossover child.',
        entityIndex: memeplexIndex,
        previousFitness: memeplex[memeplexIdx].fitness,
        fitness: children[childIdx].fitness,
        accepted: true,
        solution: children[childIdx].x,
      });
      memeplex[memeplexIdx] = children[childIdx];
      memeplexIdx--;
      crossoverChanged = true;
    }
    childIdx++;
  }

  if (crossoverChanged) {
    memeplex.sort((a, b) => b.fitness - a.fitness);
  }

  const explorationRate =
    Math.max(0.3, 1.0 - generation / Math.max(1, maxGenerations) * 0.4);
  if (eliteCount >= memeplexSize) {
    return;
  }

  let mutationAccepted = false;
  for (let i = 0; i < mutateCount; i++) {
    let idx: number;
    if (rng.randomChance(0.7)) {
      const worstHalfStart = Math.max(eliteCount, Math.floor(memeplexSize / 2));
      idx = worstHalfStart + rng.randomInt(memeplexSize - worstHalfStart);
    } else {
      idx = eliteCount + rng.randomInt(memeplexSize - eliteCount);
    }

    let mutated = mutateChromosome(
      memeplex[idx],
      taskCount,
      serviceCount,
      matrix,
      rng,
      enhancements.guidedMutation,
      heavyMode
    );
    calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);

    const acceptanceThreshold = Math.max(
      0.95,
      0.98 - generation / Math.max(1, maxGenerations) * 0.03
    );

    if (
      mutated.fitness > memeplex[idx].fitness ||
      (rng.randomChance(explorationRate) &&
        mutated.fitness >= memeplex[idx].fitness * acceptanceThreshold)
    ) {
      trace?.({
        generation: generation + 1,
        phase: 'mutation',
        action: 'accept_guided_mutation',
        detail: 'Accepted guided mutation for a memeplex member.',
        entityIndex: memeplexIndex,
        partnerIndex: idx,
        previousFitness: memeplex[idx].fitness,
        fitness: mutated.fitness,
        accepted: true,
        solution: mutated.x,
        metadata: { explorationRate, acceptanceThreshold },
      });
      memeplex[idx] = mutated;
      mutationAccepted = true;
    }
  }

  if (mutationAccepted) {
    memeplex.sort((a, b) => b.fitness - a.fitness);
  }
}

export async function runSFLAGA(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    memeplexCount: number;
    crossoverRate: number;
    mutationRate: number;
    heavyMode?: boolean;
    sflagaEnhancements?: SflagaEnhancementOptions;
  },
  onGenerationComplete?: GenerationProgressCallback,
  seed?: number,
  runtimeOptions?: {
    yieldForProgress?: boolean;
    reportEveryGeneration?: boolean;
    trace?: AlgorithmTraceCallback;
  }
): Promise<Chromosome> {
  const rng = createSeededRNG(seed);
  const shouldYieldForProgress = runtimeOptions?.yieldForProgress ?? true;
  const shouldReportEveryGeneration = runtimeOptions?.reportEveryGeneration ?? false;
  const trace = runtimeOptions?.trace;
  const heavyMode = parameters.heavyMode ?? false;
  const enhancements = resolveSflagaEnhancements(parameters.sflagaEnhancements);

  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount,
    rng
  );
  trace?.({
    generation: 0,
    phase: 'initialization',
    action: 'initialize_population',
    detail: `Created ${parameters.populationSize} frogs.`,
    metadata: { populationSize: parameters.populationSize, taskCount, serviceCount },
  });

  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }
  const initialBest = findBestChromosome(population);
  trace?.({
    generation: 0,
    phase: 'evaluation',
    action: 'evaluate_initial_population',
    detail: 'Evaluated all frogs in the initial population.',
    bestFitness: initialBest.fitness,
    solution: initialBest.x,
  });

  population.sort((a, b) => b.fitness - a.fitness);
  let memeplexes = createMemeplexes(population, parameters.memeplexCount);
  trace?.({
    generation: 0,
    phase: 'memeplex',
    action: 'create_memeplexes',
    detail: `Created ${parameters.memeplexCount} memeplexes from the sorted frog population.`,
    metadata: { memeplexCount: parameters.memeplexCount },
  });

  const bestFitnessHistory: number[] = [];
  const stagnationThreshold = 15;
  let currentMutationRate = parameters.mutationRate;
  let currentImmigrantPercent = 0.03;

  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100;

  for (let gen = 0; gen < parameters.generations; gen++) {
    const globalBest = population[0];
    let stagnationDetected = false;
    bestFitnessHistory.push(globalBest.fitness);
    if (bestFitnessHistory.length > stagnationThreshold) {
      bestFitnessHistory.shift();
    }

    if (
      enhancements.adaptiveImmigrantRefresh &&
      bestFitnessHistory.length >= stagnationThreshold
    ) {
      const recentBest = bestFitnessHistory[bestFitnessHistory.length - 1];
      const olderBest = bestFitnessHistory[0];

      if (recentBest <= olderBest * 1.01) {
        currentMutationRate = Math.min(100, currentMutationRate * 1.15);
        currentImmigrantPercent = Math.min(0.05, currentImmigrantPercent + 0.01);
        stagnationDetected = true;
        trace?.({
          generation: gen + 1,
          phase: 'adaptive_refresh',
          action: 'detect_stagnation',
          detail: 'Detected stagnant best fitness and increased mutation/immigrant refresh pressure.',
          bestFitness: recentBest,
          metadata: { currentMutationRate, currentImmigrantPercent },
        });
      } else {
        currentMutationRate = Math.max(parameters.mutationRate, currentMutationRate * 0.98);
        currentImmigrantPercent = Math.max(0.03, currentImmigrantPercent * 0.99);
      }
    } else if (!enhancements.adaptiveImmigrantRefresh) {
      currentMutationRate = parameters.mutationRate;
      currentImmigrantPercent = 0;
    }

    for (let memeplexIndex = 0; memeplexIndex < memeplexes.length; memeplexIndex++) {
      const memeplex = memeplexes[memeplexIndex];
      evolveMemeplex(
        memeplex,
        globalBest,
        taskCount,
        serviceCount,
        matrix,
        heavyMode,
        enhancements,
        parameters.selectionRate,
        parameters.crossoverRate,
        currentMutationRate,
        gen,
        parameters.generations,
        rng,
        memeplexIndex,
        trace
      );
    }

    shuffleMemeplexes(memeplexes, population);
    trace?.({
      generation: gen + 1,
      phase: 'memeplex',
      action: 'shuffle_memeplexes',
      detail: 'Interleaved memeplexes back into the population.',
    });
    population.sort((a, b) => b.fitness - a.fitness);

    const eliteCount = enhancements.elitePreservation
      ? Math.max(1, Math.floor(population.length * 0.02))
      : 0;
    const elites = population.slice(0, eliteCount).map((chrom) => ({
      x: [...chrom.x],
      fitness: chrom.fitness,
      rwp: chrom.rwp,
    }));

    if (enhancements.adaptiveImmigrantRefresh && stagnationDetected) {
      const immigrantCount = Math.max(
        1,
        Math.floor(population.length * currentImmigrantPercent)
      );
      for (let i = 0; i < immigrantCount; i++) {
        const replaceIdx = population.length - 1 - i;
        if (replaceIdx < eliteCount) {
          break;
        }

        const immigrant: Chromosome = {
          x: [...globalBest.x],
          fitness: 0,
          rwp: 0,
        };

        const mutateCount = Math.max(
          1,
          Math.floor(taskCount * (0.1 + rng.random() * 0.1))
        );
        const mutatedIndices = new Set<number>();
        for (let j = 0; j < mutateCount; j++) {
          let taskIdx: number;
          do {
            taskIdx = rng.randomInt(taskCount);
          } while (mutatedIndices.has(taskIdx));

          mutatedIndices.add(taskIdx);
          immigrant.x[taskIdx] = rng.randomInt(serviceCount);
        }

        calculateChromosomeFitness(immigrant, matrix, taskCount, serviceCount);
        population[replaceIdx] = immigrant;
        trace?.({
          generation: gen + 1,
          phase: 'adaptive_refresh',
          action: 'insert_immigrant',
          detail: 'Inserted an adaptive immigrant derived from the global best with random mutated tasks.',
          entityIndex: replaceIdx,
          fitness: immigrant.fitness,
          accepted: true,
          solution: immigrant.x,
          metadata: { mutateCount, currentImmigrantPercent },
        });
      }
    }

    for (let i = 0; i < eliteCount && i < elites.length; i++) {
      if (elites[i].fitness > population[i].fitness) {
        population[i] = elites[i];
        trace?.({
          generation: gen + 1,
          phase: 'elitism',
          action: 'restore_elite',
          detail: 'Restored preserved elite after population shuffle and refresh.',
          entityIndex: i,
          fitness: elites[i].fitness,
          accepted: true,
          solution: elites[i].x,
        });
      }
    }

    population.sort((a, b) => b.fitness - a.fitness);
    memeplexes = createMemeplexes(population, parameters.memeplexCount);
    trace?.({
      generation: gen + 1,
      phase: 'memeplex',
      action: 'recreate_memeplexes',
      detail: 'Recreated memeplexes for the next generation.',
      bestFitness: population[0]?.fitness,
      solution: population[0]?.x,
    });

    const now = Date.now();
    const shouldUpdate =
      typeof onGenerationComplete === 'function' &&
      (
        shouldReportEveryGeneration ||
        now - lastUpdateTime > UPDATE_INTERVAL ||
        gen === parameters.generations - 1
      );

    if (shouldUpdate) {
      onGenerationComplete(gen + 1, population[0].fitness, population[0].x);
      trace?.({
        generation: gen + 1,
        phase: 'generation',
        action: 'generation_complete',
        detail: 'Generation best solution recorded.',
        bestFitness: population[0].fitness,
        solution: population[0].x,
      });
      lastUpdateTime = now;
      if (shouldYieldForProgress) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }
  }

  return population[0];
}
